/// <amd-module name="Repos/ContentPreview/Markdown/Components/MarkdownPreview" />
import { MarkdownPreviewWithAutocomplete } from "Mention/MarkdownWithAutocomplete/Components";
import * as React from "react";
import { IMarkdownPreviewProps as RendererProps } from "VSS/Features/MarkdownEditor/Components";
import { PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { VssComponent } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { getService as getMarkdownLinkingService } from "../MarkdownLinkingService";
import { IMarkdownPreviewProps } from "./MarkdownPreview.Props";

class MarkdownPreview extends VssComponent<IMarkdownPreviewProps> {
    public render(): JSX.Element {
        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    const pendingWork = scenarioProps.registerPendingWork("render-markdown");
                    const { context = {}, options, rawContent, imageUrlTransformer, linkTransformer } = this.props;
                    const markdownProps: RendererProps = {
                        className: css(this.props.className, "markdown-preview-container scroll-hidden"),
                        linkClickHandler: this.linkClickHandler,
                        onInitialRender: () => {
                            // Navigate to the anchor if there is any existing in the URL
                            // Wait 500ms so images are loaded (try our best)
                            this.setTimeout(() => getMarkdownLinkingService(this.context.pageContext).navigateToAnchorFromHistory(), 500);

                            // Execute prop delegate if specified
                            const { onInitialRender } = this.props;
                            if (onInitialRender) {
                                onInitialRender();
                            }

                            // Complete the scenario
                            pendingWork && pendingWork.complete();
                        },
                        options: {
                            ...options,
                            enableHeaderAnchorSharing: true,
                            enableYaml: true,
                            html: true,
                            imageUrlTransformer,
                            linkTransformer
                        },
                        value: rawContent || context.content || ""
                    };

                    return (
                        <>
                            <Observer anchor={getMarkdownLinkingService(this.context.pageContext).getAnchor()}>
                                {(props: { anchor: string }) => {
                                    getMarkdownLinkingService(this.context.pageContext).navigateToAnchor(props.anchor);
                                    // Trying to save subscribe/unsubscribe here and returning null since we really don't
                                    // want to render anything here
                                    return null;
                                }}
                            </Observer>
                            <MarkdownPreviewWithAutocomplete {...markdownProps} />
                        </>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }

    private linkClickHandler = (event: MouseEvent): boolean => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) {
            return true;
        }

        const anchorElement = event.target as HTMLAnchorElement;
        const href = anchorElement.getAttribute("href");
        if (href) {
            if (this.props.linkParse) {
                const parsed = this.props.linkParse(href);
                if (parsed) {
                    if (parsed.path) {
                        getMarkdownLinkingService(this.context.pageContext).setRelativePath(parsed.path, parsed.anchor);
                    } else {
                        getMarkdownLinkingService(this.context.pageContext).setAnchor(parsed.anchor);
                    }

                    return false;
                }
            }

            if (href.startsWith("#")) {
                // Anchor path
                getMarkdownLinkingService(this.context.pageContext).setAnchor(href.substr(1));
                return false;
            }

            if (href.indexOf("://") < 0) {
                // Relative path within the same repo
                getMarkdownLinkingService(this.context.pageContext).setRelativePath(href);
                return false;
            }
        }

        return true;
    };
}

VssComponent.register("ms.vss-code-web.markdown-preview", MarkdownPreview);
